import type { RefObject } from "react";

import { create } from "zustand";

import type { TranscriberRef } from "@components/Transcriber";
import type { TranscriptionCallback } from "@components/Transcriber/hooks";

import type { LiveTranscriptionSegment } from "@pages/ReportStudio/AudioTranscriber/components/LiveTranscriptionFeed";
import type { CallAudioShare } from "@pages/ReportStudio/AudioTranscriber/hooks/useCallAudioShare";
import type {
  RecordingSession,
  RecordingSessionStatus,
} from "@pages/ReportStudio/AudioTranscriber/hooks/useRecordingSession";

/** Autosave state of the shared Assessment Notes, driving the "Saved …" label. */
export type NotesSaveState = "idle" | "saving" | "saved" | "error";

/**
 * Imperative controls the root recording host registers so the floating dock
 * can drive the live session without holding the MediaRecorder itself. Default
 * to no-ops so a button press in a torn-down/never-started state never throws.
 */
export type DockControls = {
  pause: () => void;
  resume: () => void;
  stop: () => void;
  /** Re-send the stopped session's recording after a failed summarize request (MDRS-551). */
  retrySummarize: () => Promise<void>;
  /** Save the unsent recording's audio to the user's device. */
  downloadRecording: () => void;
  /** Delete the unsent recording and end the session. */
  discardRecording: () => void;
};

const NOOP_CONTROLS: DockControls = {
  pause: () => {},
  resume: () => {},
  stop: () => {},
  retrySummarize: async () => {},
  downloadRecording: () => {},
  discardRecording: () => {},
};

/**
 * Progress of the stopped session's summarize request (MDRS-551). The saved
 * recording is kept until it is accepted, so `failed` means the audio is still
 * on this device and can be retried, downloaded, or discarded.
 */
export type SummarizeState = {
  status: "idle" | "sending" | "retrying" | "failed";
  /** Whether there is audio to download (false for a transcript-only session). */
  hasAudio: boolean;
};

export const IDLE_SUMMARIZE: SummarizeState = { status: "idle", hasAudio: false };

type BeginSessionArgs = {
  caseNumber: string;
  examineeName: string | null;
};

type ResumeSessionArgs = BeginSessionArgs & {
  /** Recorded time captured before the reload; the clock continues from it. */
  elapsedMs: number;
  /** The assessment's saved notes, so autosave doesn't overwrite them. */
  notes: string;
  /** Feed rows of the earlier transcript segments, shown above the live ones. */
  priorRows: LiveTranscriptionSegment[];
};

interface RecordingDockState {
  /** Case that owns the live recording, or null when idle. */
  caseNumber: string | null;
  /** Examinee display name for the owning case (dock label). */
  examineeName: string | null;
  /** Mirror of the live session status pushed from the host. */
  status: RecordingSessionStatus;
  /**
   * Drift-free, pause-aware elapsed clock (ms). Owned by the store — not a
   * counter the scribe page increments — so the dock timer keeps advancing
   * while the scribe page is unmounted. `_accumulatedMs` holds time banked
   * before the current running span; `_runningSince` is the wall-clock start
   * of the current span, or null while paused/stopped.
   */
  _accumulatedMs: number;
  _runningSince: number | null;
  /**
   * The shared Assessment Notes for the active session. A single
   * value edited from BOTH the scribe Notes field and the floating dock, and
   * autosaved to the assessment `notes` field by the RecordingHost. "Stands
   * alone" — the field is replaced with exactly this text.
   */
  notes: string;
  /** Autosave state of `notes`, mirrored into the "Saved …" label. */
  notesSaveState: NotesSaveState;
  /** Wall-clock ms of the last successful notes save, for the relative label. */
  notesLastSavedAt: number | null;
  /** Earlier transcript segments of a recording resumed after a reload. */
  priorRows: LiveTranscriptionSegment[];
  /** Whether this recording continues an interrupted one (its audio is several WebM files). */
  resumed: boolean;
  /** Host-registered imperative controls (pause/resume/stop, unsent-recording actions). */
  controls: DockControls;
  /** Summarize request for the stopped session, set by the host. */
  summarize: SummarizeState;
  setSummarize: (summarize: SummarizeState) => void;
  /**
   * Ref to the always-mounted recording-session Transcriber owned by the root
   * RecordingHost. The scribe page drives its live session through
   * this shared ref so the Azure stream survives tab-nav unmount. Null until
   * the host mounts.
   */
  transcriberRef: RefObject<TranscriberRef | null> | null;
  registerTranscriberRef: (ref: RefObject<TranscriberRef | null> | null) => void;
  /**
   * Live transcription callback the scribe page registers while it is mounted,
   * so the always-mounted host Transcriber can feed the on-tab live feed
   * (partials, segments). Null while the scribe page is unmounted — the host's
   * delegating callback then no-ops, but the transcript still accumulates
   * inside the Transcriber for the summarize-on-stop path.
   */
  liveCallback: TranscriptionCallback | null;
  registerLiveCallback: (cb: TranscriptionCallback | null) => void;
  /**
   * The live recording session, owned by the root RecordingHost so
   * it survives tab-nav. The scribe page reads it from here to render the live
   * view and drive start/stop, instead of owning `useRecordingSession` itself.
   * Null until the host mounts. Updated every render by the host — treat as a
   * snapshot, not a reactive subscription (subscribe to `status` for renders).
   */
  session: RecordingSession | null;
  registerSession: (session: RecordingSession | null) => void;
  /**
   * Telehealth call-tab audio share (MDRS-268), owned by the root
   * RecordingHost (like transcriberRef) so both the consent modal
   * (TranscribeAndUploadButtons) and the live session view (AudioTranscript)
   * read/drive the SAME share instance across tab navigation. Null until the
   * host mounts.
   */
  callAudioShare: CallAudioShare | null;
  registerCallAudioShare: (callAudioShare: CallAudioShare | null) => void;
  /**
   * True while the scribe page's in-panel live Transcript view is on screen
   * — i.e. the Transcript section is selected during a live session.
   * The dock hides while this is true (the live view is the surface) and shows
   * otherwise. Published by the scribe page; false whenever it's unmounted.
   */
  transcriptViewActive: boolean;
  setTranscriptViewActive: (active: boolean) => void;
  /**
   * Timestamp bumped when the dock's "Return to session" is pressed, so the
   * scribe page (if mounted) switches back to the Transcript section to reveal
   * the live view. 0 = never requested.
   */
  returnRequestedAt: number;
  requestReturnToTranscript: () => void;
  /**
   * Timestamp bumped to make the scribe page look again for a recording to
   * recover (MDRS-551) — e.g. Start Session on a case that still has one, which
   * a new recording would otherwise delete. 0 = never requested.
   */
  recoveryCheckRequestedAt: number;
  requestRecoveryCheck: () => void;

  /** True only while the session is live (recording or paused). */
  isActive: () => boolean;
  /** True when the given case owns the current live recording. */
  ownsCase: (caseNumber: string | null | undefined) => boolean;
  /** Clock-derived elapsed milliseconds (0 when idle). */
  elapsedMs: () => number;

  beginSession: (args: BeginSessionArgs) => void;
  /** Claim a recording resumed after a reload (AssemblyAI). */
  resumeSession: (args: ResumeSessionArgs) => void;
  /**
   * Keep the owning case's examinee name fresh. The examinee is
   * snapshotted at `beginSession`, but the case query can resolve after a
   * session starts — so the scribe page calls this when its case data loads to
   * fill/refresh the name. Only updates when `caseNumber` matches the current
   * owner, so a different case's late-loading data can't clobber the label.
   */
  syncOwnerExaminee: (caseNumber: string, examineeName: string | null) => void;
  endSession: () => void;
  setStatus: (status: RecordingSessionStatus) => void;
  /** Start (or resume) the elapsed clock from now, keeping banked time. */
  startClock: () => void;
  /** Freeze the elapsed clock, banking the current running span. */
  pauseClock: () => void;
  /** Set the shared Assessment Notes text (from scribe field or dock). */
  setNotes: (notes: string) => void;
  /** Update the notes autosave state (set by the host's autosave). */
  setNotesSaveState: (state: NotesSaveState, savedAt?: number) => void;
  registerControls: (controls: DockControls) => void;
  reset: () => void;
}

export const useRecordingDock = create<RecordingDockState>((set, get) => ({
  caseNumber: null,
  examineeName: null,
  status: "idle",
  _accumulatedMs: 0,
  _runningSince: null,
  notes: "",
  notesSaveState: "idle",
  notesLastSavedAt: null,
  priorRows: [],
  resumed: false,
  controls: NOOP_CONTROLS,
  summarize: IDLE_SUMMARIZE,
  transcriberRef: null,
  liveCallback: null,
  session: null,
  callAudioShare: null,
  transcriptViewActive: false,
  returnRequestedAt: 0,
  recoveryCheckRequestedAt: 0,

  isActive: () => {
    const { status } = get();
    return status === "recording" || status === "paused";
  },

  ownsCase: (caseNumber) => {
    const s = get();
    if (!s.isActive() || !s.caseNumber || !caseNumber) return false;
    return s.caseNumber === caseNumber;
  },

  elapsedMs: () => {
    const { _accumulatedMs, _runningSince } = get();
    return _runningSince === null
      ? _accumulatedMs
      : _accumulatedMs + (Date.now() - _runningSince);
  },

  beginSession: ({ caseNumber, examineeName }) =>
    set({
      caseNumber,
      examineeName,
      notes: "",
      notesSaveState: "idle",
      notesLastSavedAt: null,
      priorRows: [],
      resumed: false,
      _accumulatedMs: 0,
      _runningSince: null,
    }),

  resumeSession: ({ caseNumber, examineeName, elapsedMs, notes, priorRows }) =>
    set({
      caseNumber,
      examineeName,
      notes,
      notesSaveState: "idle",
      notesLastSavedAt: null,
      priorRows,
      resumed: true,
      _accumulatedMs: Math.max(0, elapsedMs),
      _runningSince: null,
    }),

  syncOwnerExaminee: (caseNumber, examineeName) =>
    set((s) =>
      // Only refresh the current owner's name, and only with a real value —
      // never null out a name we already have (the query can briefly return
      // undefined between fetches).
      s.caseNumber === caseNumber && examineeName ? { examineeName } : {}
    ),

  endSession: () =>
    set({
      caseNumber: null,
      examineeName: null,
      status: "idle",
      _accumulatedMs: 0,
      _runningSince: null,
      notes: "",
      notesSaveState: "idle",
      notesLastSavedAt: null,
      priorRows: [],
      resumed: false,
      summarize: IDLE_SUMMARIZE,
    }),

  setStatus: (status) => set({ status }),

  setSummarize: (summarize) => set({ summarize }),

  startClock: () =>
    set((s) =>
      // Idempotent: don't restart an already-running span (would drop banked
      // time). Only (re)anchor when currently frozen.
      s._runningSince === null ? { _runningSince: Date.now() } : {}
    ),

  pauseClock: () =>
    set((s) => {
      if (s._runningSince === null) return {};
      return {
        _accumulatedMs: s._accumulatedMs + (Date.now() - s._runningSince),
        _runningSince: null,
      };
    }),

  setNotes: (notes) => set({ notes }),

  setNotesSaveState: (notesSaveState, savedAt) =>
    set(
      savedAt !== undefined
        ? { notesSaveState, notesLastSavedAt: savedAt }
        : { notesSaveState }
    ),

  registerControls: (controls) => set({ controls }),

  registerTranscriberRef: (transcriberRef) => set({ transcriberRef }),

  registerLiveCallback: (liveCallback) => set({ liveCallback }),

  registerSession: (session) => set({ session }),

  registerCallAudioShare: (callAudioShare) => set({ callAudioShare }),

  setTranscriptViewActive: (transcriptViewActive) => set({ transcriptViewActive }),

  requestReturnToTranscript: () => set({ returnRequestedAt: Date.now() }),

  requestRecoveryCheck: () =>
    set((s) => ({
      // Strictly increasing, so two requests in the same millisecond both count.
      recoveryCheckRequestedAt: Math.max(Date.now(), s.recoveryCheckRequestedAt + 1),
    })),

  reset: () =>
    set({
      caseNumber: null,
      examineeName: null,
      status: "idle",
      _accumulatedMs: 0,
      _runningSince: null,
      notes: "",
      notesSaveState: "idle",
      notesLastSavedAt: null,
      priorRows: [],
      resumed: false,
      controls: NOOP_CONTROLS,
      summarize: IDLE_SUMMARIZE,
      transcriptViewActive: false,
      recoveryCheckRequestedAt: 0,
    }),
}));
